import React, { useCallback } from 'react'
import { Button, Select, SelectItem, MultiSelectComboBox, MultiSelectComboBoxItem, TooltipWithTrigger } from '@calabriocloud/beacon-ui'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import axios from 'axios'
import { actionKey, contactLevel } from '../../../constants'
import { Assignee, AssigneeMappingField, Who } from '../../../constants'
import { ErrorMessage, useFormikContext } from 'formik'
import { Goal } from '../../../constants'
const ViewGoalAssigneeMapedTeamEndpoint = '/api/rest/org/common/team/scope/'
const ViewGoalAssigneeMapedUserEndpoint = '/api/rest/org/common/person/scope/'

interface UserAssignmentRowProps {
  mapping: AssigneeMappingField,
  index: number,
  assignees: Assignee[],
  onChange: (value: AssigneeMappingField) => void,
  onRemoveRow: (id: number) => void,
  onBlur?: (e: React.FocusEvent<Element>) => void,
}
interface userAPIResponse {
  personId: number,
  firstName: string,
  lastName: string,
  tenantId: number,
  displayId: string,
  email: string,
}
interface teamApiResponse {
  groupId: number,
  name: string,
  parentGroupId: number,
  parentGroupName: string,
  groupLevel: string,
}

export const UserAssignmentRow = React.memo(
  ({ mapping, index, assignees, onChange, onRemoveRow, onBlur }: UserAssignmentRowProps) => {

    const { t } = useTranslation()
    const selectedKeys = mapping.whoIds.map(item => item.id) || []
    const formik = useFormikContext<Goal>()
    const { whoType, contactGoalActionId } = formik.values
    const { isDisabled, isLegacyGoal } = formik.status

    const { data: AssigneeMappingOptions, isFetching } = useQuery<Who[]>({
      queryKey: ['scopedWhoIdsForAssignee', mapping.assignee.id, whoType],
      queryFn: async () => {
        if (!whoType || contactGoalActionId === null || !mapping.assignee.id) {
          return []
        }
        if (contactLevel.find(e => e.id === whoType)?.name === 'agent') {
          const res = await axios.get<userAPIResponse[]>(ViewGoalAssigneeMapedUserEndpoint + mapping.assignee.id)
          return res.data.map(item => ({ id: item?.personId, textValue: item.displayId }))
        } else {
          const res = await axios.get<teamApiResponse[]>(ViewGoalAssigneeMapedTeamEndpoint + mapping.assignee.id)
          return res.data.map(item => ({ id: item?.groupId, textValue: item.name }))
        }
      },
      deps: [mapping.assignee.id, whoType],
    })

    const handleAssigneeChange = useCallback(
      (assigneesId: string) => {
        const assignee = assignees?.find(e => e.id === assigneesId) || { id: '', name: '' }
        onChange({
          ...mapping,
          whoIds: [],
          assignee,
        })

      },
      [assignees],
    )

    const handleRemoveRow = useCallback(
      () => {
        onRemoveRow(mapping.rowId)
      },
      [],
    )

    const handleAddWho = useCallback(
      (key: number) => {
        const whoId = AssigneeMappingOptions?.find(e => e.id === key) || { id: key, textValue: '' }
        onChange({
          ...mapping,
          whoIds: [...mapping.whoIds, whoId],
        })
      },
      [mapping, AssigneeMappingOptions],
    )

    const handleRemoveWho = useCallback(
      (key: number) => {
        const whoIds = mapping.whoIds.filter(who => who.id !== key)
        onChange({
          ...mapping,
          whoIds,
        })
      },
      [mapping],
    )

    return (
      <div className="flex gap-2 mb-4 items-center"  {...(isLegacyGoal ? { 'data-testid': 'is-legacy-goal' } : {})}>
        <div>
          <Select
            className="w-80"
            label={t('selectAssignee')}
            data-testid={`assignee-select-${index}`}
            isDisabled={!whoType || isDisabled}
            selectedKey={mapping.assignee.id || '-1'}
            items={assignees || []}
            onBlur={onBlur}
            onSelectionChange={key => {
              handleAssigneeChange(key?.toString())
            }}
          >
            {item => (
              <SelectItem key={item.id} id={item.id}>
                {item.name}
              </SelectItem>
            )}
          </Select>
          {!mapping.assignee.id &&
            <ErrorMessage name='assigneeToWhoIds'>
              {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
            </ErrorMessage>}
        </div>
        <div className="w-60">
          <p className="text-l text-gray-900 mt-6 text-center">{contactGoalActionId !== null ? t(actionKey[contactGoalActionId] || '') : t('selectActivity')}</p>
        </div>
        <div className="w-80" >
          <TooltipWithTrigger content={t('noTeamsInScope')} tooltipProps={{ placement: 'right', className: `${(whoType === 2 && AssigneeMappingOptions?.length === 0) ? '' : 'hidden'}` }}>
            <MultiSelectComboBox
              data-testid={`whoIds-select-${index}`}
              label={whoType === 1 ? t('selectAgent') : t('selectTeam')}
              items={isFetching ? [] : AssigneeMappingOptions || []}
              selectedKeys={selectedKeys}
              onAdd={key => {
                if (typeof key === 'number') {
                  handleAddWho(key)
                } else {
                  handleAddWho(parseInt(key))
                }
              }}
              onRemove={key => {
                if (typeof key === 'number') {
                  handleRemoveWho(key)
                } else {
                  handleRemoveWho(parseInt(key))
                }
              }}
              isDisabled={isDisabled || !mapping.assignee.id || contactGoalActionId === null || AssigneeMappingOptions?.length === 0}
              onBlur={onBlur}
            >
              {item => (
                <MultiSelectComboBoxItem id={item.id}>
                  {item.textValue}
                </MultiSelectComboBoxItem>
              )}
            </MultiSelectComboBox>
          </TooltipWithTrigger>
          {!mapping.whoIds.length &&
            <ErrorMessage name='assigneeToWhoIds'>
              {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
            </ErrorMessage>}
        </div>
        {!isDisabled && (index > 0 || (index === 0 && selectedKeys.length > 0)) && (
          <Button
            className="w-fit mt-6 ml-5"
            data-testid={`user-assignment-delete-${index}-button`}
            variant="destructive"
            onPress={handleRemoveRow}
          >
            {t('Remove')}
          </Button>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.mapping === nextProps.mapping &&
      prevProps.index === nextProps.index &&
      prevProps.assignees === nextProps.assignees
    )
  },
)

UserAssignmentRow.displayName = 'UserAssignmentRow'

